import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { API } from 'api';
import { SECTIONS, DOMAINS, IMPORTANCE } from '@repo/monitoring';
import { toast } from 'react-toastify';
import { apiBasePath, apiName } from 'assets';
import { ProfileQuestionAnswer } from 'types/ProfileQuestions';

const postQuestionAnswers = async (
  answers: ProfileQuestionAnswer[],
  tinyAccountId: string,
  kitId?: string,
  isEditMode?: boolean,
  confirmDelete?: boolean,
) => {
  if (!tinyAccountId) throw new Error('No account provided');
  if (!answers?.length) throw new Error('No answer provided');
  return API.post(
    apiName,
    `${apiBasePath}/tinyaccounts/${tinyAccountId}/profile-answers${kitId ? `?kit_id=${kitId}` : ''}${confirmDelete ? `${kitId ? '&' : '?'}confirmDelete=1` : ''}${isEditMode ? `${kitId || confirmDelete ? '&' : '?'}isEditMode=1` : ''}`,
    {
      body: answers,
    },
    true,
    undefined,
    {
      tags: {
        section: SECTIONS.PROFILE,
        domain: DOMAINS.SURVEYS,
        importance: IMPORTANCE.HIGH,
      },
      context: {
        tinyAccountId,
        kitId,
        answerCount: answers.length,
        isEditMode: !!isEditMode,
        confirmDelete: !!confirmDelete,
      },
    },
  );
};

type UpdateProfileAnswerMutation = {
  answers: ProfileQuestionAnswer[];
  tinyAccountId: string;
  kitId?: string;
  isEditMode?: boolean;
  confirmDelete?: boolean;
};

type UpdateProfileAnswerHookResult = ReturnType<
  typeof useMutation<void, unknown, UpdateProfileAnswerMutation>
> & {
  modalProps: {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    description: string;
  };
};

const useUpdateProfileAnswer = (): UpdateProfileAnswerHookResult => {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [pendingAnswers, setPendingAnswers] = useState<{
    answers: ProfileQuestionAnswer[];
    tinyAccountId: string;
    kitId?: string;
  } | null>(null);

  const profileAnswerMutation = useMutation<
    void,
    unknown,
    UpdateProfileAnswerMutation
  >({
    mutationFn: (params: UpdateProfileAnswerMutation) => {
      return postQuestionAnswers(
        params.answers,
        params.tinyAccountId,
        params.kitId,
        params.isEditMode,
        params.confirmDelete,
      );
    },
    onError: (error: unknown, variables) => {
      if (error instanceof AxiosError) {
        if (error.response?.status === 409) {
          setModalMessage(error.response.data.message);
          setModalOpen(true);
          setPendingAnswers({
            answers: variables.answers,
            tinyAccountId: variables.tinyAccountId,
            kitId: variables.kitId,
          });
        } else if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error('An error occurred while saving your answers');
        }
      } else {
        toast.error('An error occurred while saving your answers');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: query => query.queryKey.includes('profileQuestions'),
      });
      queryClient.invalidateQueries({
        predicate: query => query.queryKey.includes('profileSurveys'),
      });
    },
  });
  const handleConfirmDelete = async () => {
    if (pendingAnswers) {
      try {
        await profileAnswerMutation.mutateAsync({
          ...pendingAnswers,
          isEditMode: true,
          confirmDelete: true,
        });
        setModalOpen(false);
        setPendingAnswers(null);
      } catch (error) {
        if (error instanceof AxiosError && error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error('An error occurred while saving your answers');
        }
      }
    }
  };

  return {
    ...profileAnswerMutation,
    modalProps: {
      isOpen: modalOpen,
      onClose: () => {
        setModalOpen(false);
        setPendingAnswers(null);
      },
      onConfirm: handleConfirmDelete,
      title: 'Confirm Changes',
      description: modalMessage,
    },
  };
};

export default useUpdateProfileAnswer;
